/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, { Component } from "react";
import { PageNumberUIContainer } from "containers/hud/PageNumberUIContainer";
import { DockHUDContainer } from "containers/hud/DockHUDContainer";
import HUDViewCss from "./HUDView.css";
import { apiFactory } from "api/external/ApiFactory";
import { apiNames } from "constants/apiNames";
import {
  HUD_HEIGHT,
  HUD_BOTTOM_MARGIN,
  HUD_PROGRESS_BAR_OPACITY,
  HUD_BOTTOM_MARGIN_MODERNVIEWER,
  TOOLTIP_TOP_PLACEMENT
} from "constants/constants";
import { ZoomUIContainer } from "containers/hud/ZoomUIContainer";
import { PageViewModifierUIContainer } from "containers/hud/PageViewModifierUIContainer";
import { RotateViewButtonContainer } from "containers/hud/RotateViewButtonContainer";
import { PreviousPageNavigationContainer } from "containers/hud/PreviousPageNavigationContainer";
import { NextPageNavigationContainer } from "containers/hud/NextPageNavigationContainer";
import { ExternalComponentContainer } from "containers/hud/ExternalComponentContainer";
import { OverFlowMenuContainer } from "containers/hud/OverFlowMenuContainer";
import classNames from "classnames";
import { SeparatorView } from "components/common/SeparatorView";
import { SkipNavContainer } from "containers/SkipNavContainer";
import { PrintButtonContainer } from "print/components/PrintButtonContainer";
import { SearchWrapperContainer } from "containers/search/SearchWrapperContainer";
import { DownloadButtonContainer } from "containers/topbar/DownloadButtonContainer";
import * as stores from "stores";
import { getIsInPlaceMode } from "selectors/viewModeSelectors";
import { isSinglePageMode } from "selectors/documentSelectors";
import { hudUiControlAlignments, animationType } from "constants/enums";
import { getShouldUpdateHUDPosition } from "selectors/hudSelectors";
import { hudHideManager } from "utils/ComponentHideManager";
import CommonCss from "../common/common.css";
import { isCurrentDeviceMobile } from "dc-preview-utils/src/Utils/DeviceUtils";

class HUDView extends Component {
  constructor(props) {
    super(props);

    Object.assign(this, props);
    this._bindApis();
    this._registerApis();
    this.isCurrentDeviceMobile = isCurrentDeviceMobile();
  }

  _apiName() {
    return apiNames.HUD;
  }

  _bindApis() {
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
  }

  _registerApis() {
    apiFactory.registerApi(this._apiName(), clientName => ({
      setHUDVisibility: this.props.setHudDisable,
      setPageNavigationVisibility: this.props.setPageNavigationVisibility,
      setPageNumberVisibility: this.props.setPageNumberVisibility,
      setZoomControlsVisibility: this.props.setZoomControlsVisibility,
      setZoomUIVisibility: this.props.setZoomUIVisibility,
      setFitWidthModeVisibility: this.props.setFitWidthModeVisibility,
      setFitPageModeVisibility: this.props.setFitPageModeVisibility,
      setFixedWidthVisibility: this.props.setFixedWidthVisibility,
      setPrintVisibility: this.props.setPrintVisibility,
      setRotateViewButtonVisibility: this.props.setRotateViewButtonVisibility
    }));
  }

  searchGroup() {
    const itemList = [];

    let optionsObj = {};
    if (this.props.useModernViewer) {
      optionsObj = { color: "light" };
    } else {
      optionsObj = { color: "dark" };
    }
    this.props.showSearchUI &&
      this.props.enableSearchFeature &&
      itemList.push(
        <SearchWrapperContainer
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          options={optionsObj}
          buttonStyle={{
            marginRight: 8
          }}
        />
      );

    return itemList;
  }

  getPageNumberUIContainer() {
    return (
      <PageNumberUIContainer
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        ref="pageNumberInput"
        intl={this.props.intl}
        inputTooltipPlacement={TOOLTIP_TOP_PLACEMENT}
        totalPagesTooltipPlacement={TOOLTIP_TOP_PLACEMENT}
      />
    );
  }

  pageNavigationGroup() {
    const itemList = [];
    const { showPageNavigation, showPageNumber } = this.props;
    if (getIsInPlaceMode(stores)) {
      showPageNavigation && itemList.push(<PreviousPageNavigationContainer tooltipPlacement={TOOLTIP_TOP_PLACEMENT} />);
      showPageNumber && itemList.push(this.getPageNumberUIContainer());
      showPageNavigation &&
        itemList.push(
          <NextPageNavigationContainer
            containerStyle={{ marginRight: "8px" }}
            tooltipPlacement={TOOLTIP_TOP_PLACEMENT}
          />
        );
    } else if (isSinglePageMode(stores)) {
      showPageNavigation &&
        itemList.push(
          <PreviousPageNavigationContainer
            containerStyle={{ transform: "rotate(270deg)" }}
            tooltipPlacement={TOOLTIP_TOP_PLACEMENT}
          />
        );
      showPageNumber && itemList.push(this.getPageNumberUIContainer());
      showPageNavigation &&
        itemList.push(
          <NextPageNavigationContainer
            containerStyle={{ transform: "rotate(270deg)", marginRight: "8px" }}
            tooltipPlacement={TOOLTIP_TOP_PLACEMENT}
          />
        );
    } else {
      showPageNavigation && itemList.push(<PreviousPageNavigationContainer tooltipPlacement={TOOLTIP_TOP_PLACEMENT} />);
      showPageNavigation && itemList.push(<NextPageNavigationContainer tooltipPlacement={TOOLTIP_TOP_PLACEMENT} />);
      showPageNumber && itemList.push(this.getPageNumberUIContainer());
    }
    return itemList;
  }

  pageNavigationGroupModern() {
    const itemList = [];
    if (this.props.showPageNavigation) {
      if (getIsInPlaceMode(stores)) {
        itemList.push(<PreviousPageNavigationContainer tooltipPlacement={TOOLTIP_TOP_PLACEMENT} />);
        itemList.push(
          <NextPageNavigationContainer
            containerStyle={{ marginRight: "8px" }}
            tooltipPlacement={TOOLTIP_TOP_PLACEMENT}
          />
        );
      } else if (isSinglePageMode(stores)) {
        itemList.push(
          <PreviousPageNavigationContainer
            containerStyle={{ transform: "rotate(270deg)" }}
            tooltipPlacement={TOOLTIP_TOP_PLACEMENT}
          />
        );
        itemList.push(
          <NextPageNavigationContainer
            containerStyle={{ transform: "rotate(270deg)", marginRight: "8px" }}
            tooltipPlacement={TOOLTIP_TOP_PLACEMENT}
          />
        );
      }
    }
    return itemList;
  }

  pageNumberGroup() {
    const itemList = [];
    if (this.props.showPageNumber) {
      itemList.push(this.getPageNumberUIContainer());
    }
    return itemList;
  }

  zoomUIGroup() {
    const itemList = [];
    this.props.showZoomInOut && itemList.push(<ZoomUIContainer />);
    return itemList;
  }

  pageViewModifierUIGroup() {
    const itemList = [];
    this.props.showZoomUI && itemList.push(<PageViewModifierUIContainer />);
    return itemList;
  }

  rotateUIGroup() {
    const itemList = [];
    this.props.enableRotation && itemList.push(<RotateViewButtonContainer />);
    return itemList;
  }

  miscButtonsGroup(iconColor = "dark") {
    const itemList = [];
    this.props.showPrint &&
      !this.props.showOverflowMenuButton &&
      itemList.push(<PrintButtonContainer options={{ color: iconColor }} />);
    this.props.showDownloadButton &&
      !this.props.showOverflowMenuButton &&
      itemList.push(<DownloadButtonContainer options={{ color: iconColor }} />);
    this.props.showDockButton && itemList.push(<DockHUDContainer />);
    this.props.showOverflowMenuButton && itemList.push(<OverFlowMenuContainer />);

    return itemList;
  }

  rightGroupComponents() {
    let itemList = [];
    let componentList = [];

    const searchGroup = this.searchGroup();
    if (searchGroup.length > 0) {
      componentList.push(searchGroup);
    }
    const miscComponents = this.miscButtonsGroup("light");
    if (miscComponents.length > 0) {
      componentList.push(miscComponents);
    }
    if (isSinglePageMode(stores)) {
      const componentClasses = this.getClassnamesForExternalComponent("RIGHT");
      itemList.push(<div className={componentClasses}>{componentList}</div>);
    } else if (getIsInPlaceMode(stores)) {
      itemList = componentList;
    }
    return itemList;
  }

  getClassnamesForExternalComponent(alignment) {
    const { isDocked } = this.props;
    let className;
    if (alignment === hudUiControlAlignments.LEFT) {
      className = classNames(
        className,
        { [`${HUDViewCss.externalComponentLeftDocked}`]: isDocked },
        { [`${HUDViewCss.externalComponentLeftUndocked}`]: !isDocked && !this.props.useModernViewer },
        { [`${HUDViewCss.externalComponentLeftUndockedModern}`]: !isDocked && this.props.useModernViewer }
      );
    } else if (alignment === hudUiControlAlignments.RIGHT) {
      className = classNames(
        className,
        { [`${HUDViewCss.externalComponentRightDocked}`]: isDocked },
        { [`${HUDViewCss.externalComponentRightUndocked}`]: !isDocked }
      );
    }
    return className;
  }

  getExternalComponents(alignment) {
    const itemList = [],
      componentList = [];
    let componentClasses = this.getClassnamesForExternalComponent(alignment);
    const extComponents = this.props.getExternalComponentsFromAlignment(alignment);
    extComponents &&
      extComponents.forEach(extComponent => {
        const component = extComponent.component;
        if (typeof component === "object") {
          if (this.props.showOverflowMenuButton ? alignment === "LEFT" || alignment === "CENTER" : true) {
            if (component.icon && component.title && component.clickHandler) {
              componentList.push(
                <ExternalComponentContainer
                  icon={component.icon}
                  clickHandler={component.clickHandler}
                  title={component.title}
                />
              );
            }
          }
        } else {
          const Component = component;
          componentList.push(<Component />);
        }
      });

    componentList.length && itemList.push(<div className={componentClasses}>{componentList}</div>);

    return itemList;
  }

  getRenderGroupList() {
    const externalComponentsOnLeftGroup = this.getExternalComponents(hudUiControlAlignments.LEFT);
    const searchGroup = this.searchGroup();
    const pageNavigationGroup = this.pageNavigationGroup();
    const zoomUIGroup = this.zoomUIGroup();
    const pageViewModifierUIGroup = this.pageViewModifierUIGroup();
    const rotateUIGroup = this.rotateUIGroup();
    const dockSwitchGroup = this.miscButtonsGroup();
    const externalComponentsOnRightGroup = this.getExternalComponents(hudUiControlAlignments.RIGHT);
    let groupList = [
      externalComponentsOnLeftGroup,
      pageNavigationGroup,
      zoomUIGroup,
      pageViewModifierUIGroup,
      rotateUIGroup,
      dockSwitchGroup,
      externalComponentsOnRightGroup
    ];

    getIsInPlaceMode(stores) ? groupList.splice(2, 0, searchGroup) : groupList.splice(1, 0, searchGroup);

    if (this.props.getIsSearchInputUIVisibleInHUD && getIsInPlaceMode(stores)) {
      groupList = [searchGroup];
    }

    return groupList.filter(item => item && item.length > 0);
  }

  getRenderGroupListModernViewer() {
    // in modern viewer, currently HUD is getting used for inline and sized container modes only
    const leftGroupExternalComps = this.getExternalComponents(hudUiControlAlignments.LEFT); // branding icon
    const pageNavigationGroup = this.pageNavigationGroupModern();
    const pageNumberGroup = this.pageNumberGroup();
    const centerGroupExternalComps = this.getExternalComponents(hudUiControlAlignments.CENTER); // full screen icon in sized container mode
    const rightGroupComps = this.rightGroupComponents();

    let groupList = [
      leftGroupExternalComps,
      pageNavigationGroup,
      pageNumberGroup,
      centerGroupExternalComps,
      rightGroupComps
    ];

    let hudComponents = [];
    if (this.props.getIsSearchInputUIVisibleInHUD) {
      groupList = this.searchGroup();
      if (groupList.length > 0) {
        hudComponents.push(groupList);
      }
    } else {
      groupList = groupList.filter(item => item && item.length > 0);
      let separatorStart = 1;
      let separatorEnd = groupList.length - 1;
      if (isSinglePageMode(stores)) {
        separatorStart = leftGroupExternalComps.length > 0 ? separatorStart + 1 : separatorStart;
        separatorEnd = rightGroupComps.length > 0 ? separatorEnd - 1 : separatorEnd;
      }
      for (let i = 0; i < separatorStart; i++) {
        hudComponents.push(groupList[i]);
      }
      for (let i = separatorStart; i <= separatorEnd; i++) {
        hudComponents.push(<SeparatorView useModernViewer />);
        hudComponents.push(groupList[i]);
      }
      for (let i = separatorEnd + 1; i < groupList.length; i++) {
        hudComponents.push(groupList[i]);
      }
    }

    return hudComponents;
  }

  shouldAddSeparatorView(index, total) {
    let result = index !== 0;
    if (this.props.isDocked) {
      result =
        result &&
        !(index === total && this.getExternalComponents(hudUiControlAlignments.RIGHT).length) &&
        !(index === 1 && this.getExternalComponents(hudUiControlAlignments.LEFT).length);
    }
    if (getIsInPlaceMode(stores) && index === total) {
      result = false;
    }
    return result;
  }

  renderHudComponents() {
    let hudComponents = [];
    if (this.props.useModernViewer) {
      hudComponents = this.getRenderGroupListModernViewer();
    } else {
      const renderGroupList = this.getRenderGroupList();
      for (let i = 0; i < renderGroupList.length; i++) {
        if (this.shouldAddSeparatorView(i, renderGroupList.length - 1)) {
          hudComponents.push(<SeparatorView />);
        }
        hudComponents.push(renderGroupList[i]);
      }
    }
    return hudComponents;
  }

  componentWillUnmount() {
    this.props.cleanup();
  }

  componentDidMount() {
    stores.hudStore.setMouseOver(false);
    if (this.hudContainerHandle) {
      this.props.setHUDView(this.hudContainerHandle);
      this.hudContainerHandle.addEventListener("touchmove", this.props.onTouchMove, { passive: false });
      this.props.init(this.hudContainerHandle);
    }

    this.props.disableContextMenu && this.props.disableContextMenu(HUDViewCss.hudContainer);
  }

  onFocus() {
    this.props.setFocusOnHud(true);
  }

  onBlur() {
    if (!this.props.isDocked) {
      hudHideManager.resurfaceHud(stores);
    }
    this.props.setFocusOnHud(false);
  }

  shouldComponentUpdate() {
    if (getIsInPlaceMode(stores) && !getShouldUpdateHUDPosition(stores)) {
      return false;
    }
    return true;
  }

  componentDidUpdate() {
    if (this.hudContainerHandle) {
      this.props.init(this.hudContainerHandle);
      this.props.setHUDWidth(this.hudContainerHandle.clientWidth);
    }
  }

  render() {
    const { showHUD, isHUDEnabled, isDocked, docVisibleHeight, isAutoHiding } = this.props;
    let { left } = this.props;

    let bottom = HUD_BOTTOM_MARGIN;
    let backgroundColor = "";

    let className = HUDViewCss.HUDView;
    if (this.props.useModernViewer) {
      className = classNames(className, HUDViewCss.HUDViewModern);
    }
    if (isDocked) {
      bottom = 0;
      backgroundColor = "var(--spectrum-global-color-gray-900, #2C2C2C)";
      left = 0;
      className = classNames(className, HUDViewCss.docked);
    }
    if (this.props.useModernViewer) {
      backgroundColor = "var(--spectrum-global-color-gray-75, #FAFAFA)";
      if (getIsInPlaceMode(stores)) {
        backgroundColor = "transparent";
        bottom = HUD_BOTTOM_MARGIN_MODERNVIEWER;
      }
    }

    let hudContainerClass = classNames(null, HUDViewCss.hudContainer);

    if (this.props.isImmersiveMode) {
      hudContainerClass = classNames(hudContainerClass, HUDViewCss.immersiveHide);
    } else {
      hudContainerClass = classNames(hudContainerClass, HUDViewCss.immersiveShow);
    }

    let style = {
      position: "absolute",
      bottom: `${bottom}px`,
      backgroundColor,
      left: `${left}px`
    };

    let innerDivStyle = {};
    if (getIsInPlaceMode(stores)) {
      if (this.props.totalPages > 99) {
        innerDivStyle.padding = "0px 0px";
      }
    }

    if (this.props.applyAnimationClasses) {
      // HUD slide in/out case
      let animationString = "";
      let animationInStyle, animationOutStyle;
      if (this.props.animationType === animationType.SLIDE) {
        animationOutStyle = CommonCss.slideOut;
        animationInStyle = CommonCss.slideIn;
      } else if (this.props.animationType === animationType.FADE) {
        if (isSinglePageMode(stores) && isDocked) {
          animationInStyle = CommonCss.fadeInPartial;
          animationOutStyle = CommonCss.fadeOutPartial;
        } else {
          animationInStyle = CommonCss.fadeInFull;
          animationOutStyle = CommonCss.fadeOutFull;
        }
      }

      if (!isAutoHiding) {
        animationString = `${animationInStyle} ${this.props.animationInDuration / 1000}s linear`;
      } else {
        animationString = `${animationOutStyle} ${this.props.animationOutDuration / 1000}s linear`;
      }
      style.animation = animationString;
      if (this.props.animationType === animationType.FADE) innerDivStyle.animation = animationString;
    } else {
      // normal fade in/fade out case of HUD undocked: resurfaceHUD
      delete style.animation;
      if (showHUD) {
        className = classNames(className, HUDViewCss.HUDViewShow);
      } else {
        className = classNames(className, HUDViewCss.HUDViewHide);
        hudContainerClass = classNames(hudContainerClass, HUDViewCss.hideBorder);
      }
    }

    if (isDocked) {
      style.width = this.props.dockedWidth;
    } else {
      delete style.backgroundColor;
    }

    if (getIsInPlaceMode(stores)) {
      style.top = `${docVisibleHeight - bottom - HUD_HEIGHT}px`;
    }

    if (!this.props.useModernViewer && isSinglePageMode(stores) && isDocked) {
      style.opacity = HUD_PROGRESS_BAR_OPACITY;
    }

    if (this.props.useModernViewer && this.props.getIsSearchInputUIVisibleInHUD) {
      innerDivStyle.width = "100%"; // to use all available width in sized container mode
    }

    return isHUDEnabled ? (
      <div
        className={hudContainerClass}
        style={style}
        ref={handle => {
          this.hudContainerHandle = handle;
        }}
      >
        <div className={className} ref={handle => (this.elementHandle = handle)} tabIndex="-1" style={innerDivStyle}>
          {this.renderHudComponents()}
          <SkipNavContainer intl={this.props.intl} />
        </div>
      </div>
    ) : this.isCurrentDeviceMobile ? null : (
      <SkipNavContainer intl={this.props.intl} />
    );
  }
}

export { HUDView };
